/* ============================================================
   DATA MATERI EDUKASI

   Letakkan di: app/edukasi-kesehatan/data.js

   Berkas ini SENGAJA dipisah dari tampilannya. Isinya akan sering
   ditambah - tiap ada leaflet baru - sementara EdukasiContent.jsx
   hampir tidak pernah disentuh lagi. Memisahkannya berarti menambah
   materi tidak pernah berisiko merusak tata letak.

   ============================================================
   >>> DUA HAL YANG HARUS KAMU KERJAKAN SEBELUM HALAMAN INI JALAN <<<

   1. ⚠️ COCOKKAN NOMOR BERKAS DENGAN TOPIKNYA. INI YANG PALING
      PENTING, DAN HANYA KAMU YANG BISA MENGECEKNYA.

      Berkasmu bernama 1.png sampai 18.png - angka saja, tidak
      memberi tahu apa isinya. Saya memetakannya berdasarkan URUTAN
      TAMPIL di situs lama yang kamu kirim tangkapan layarnya:

        1-2    KET                  11-12  Diare
        3-4    Mioma Uteri          13-14  Ketoasidosis Diabetikum
        5-6    Hemodialisa          15-16  Stroke
        7-8    Kejang Demam Anak    17-18  CAPD
        9-10   GERD

      ITU ASUMSI, BUKAN FAKTA. Kalau berkasnya diunduh tidak berurutan,
      pemetaannya meleset - dan akibatnya bukan sekadar tampilan salah:
      pengunjung yang membuka kartu "Stroke" bisa mendapat leaflet
      CAPD. Untuk materi kesehatan, itu kesalahan yang serius.

      CARA MENGECEK, dua menit: buka /edukasi-kesehatan, cocokkan judul
      tiap kartu dengan gambarnya. Yang meleset tinggal ditukar
      angkanya di `halaman` di bawah.

      SARAN SETELAH COCOK: ganti nama berkasnya jadi berbicara -
      stroke-1.png, capd-2.png - lalu perbarui path di sini. Dengan
      angka berurutan, masalah ini terulang setiap ada leaflet baru
      atau ada berkas yang tertimpa.

      ⚠️ ADA BERKAS "Unconfirmed 927532.crdownload" DI FOLDER ITU.
      Itu unduhan Chrome yang belum selesai. Hapus - ia tidak dipakai,
      dan kalau tertinggal ia ikut ter-deploy ke server.

   2. TEKS `alt`. Semua masih kosong. Ini bukan detail kecil di
      halaman seperti ini: seluruh isi leaflet adalah teks yang
      dibakar ke dalam gambar, jadi `alt` adalah SATU-SATUNYA cara
      pengguna tunanetra dan mesin pencari tahu isinya.

      Isi dengan ringkasan isi leafletnya, bukan judulnya lagi.
      Contoh untuk sisi depan leaflet stroke:
        "Kenali gejala stroke dengan metode SEGERA: senyum tidak
         simetris, gerak lemah sebelah, bicara pelo. Segera ke rumah
         sakit dalam waktu kurang dari 2 jam."
      Bukan: "Leaflet stroke 1".

      Selama kosong, alt terisi kalimat cadangan yang lolos secara
      teknis tapi tidak berguna bagi siapa pun.

   ============================================================
   BENTUK DATANYA

   judul & ringkas  -> { id, en }. Boleh juga string biasa kalau sama
                       di kedua bahasa. Kalau `en` kosong, versi
                       Indonesia yang dipakai - materi baru tidak akan
                       pernah tampil bolong.
   kategori         -> "leaflet" | "flyer" | "poster"
   halaman          -> daftar sisi. Leaflet lipat tiga biasanya 2 sisi
                       (depan & belakang). Poster biasanya 1.

   KENAPA DUA SISI DIGABUNG JADI SATU KARTU:
   Di situs lama, "Leaflet KET 1" dan "Leaflet KET 2" tampil sebagai
   dua kartu terpisah - padahal keduanya satu lembar yang sama, depan
   dan belakang. Pengunjung yang membuka salah satunya tidak punya
   petunjuk bahwa ada sisi lainnya. Digabung, ia jadi satu materi
   dengan dua halaman, dan panah di lightbox membawanya ke sisi
   sebaliknya.

   Kalau kamu tetap ingin terpisah, buat dua entri masing-masing
   dengan satu halaman - strukturnya sudah mendukung keduanya.
   ============================================================ */

/* ============================================================
   KATEGORI

   `rasio` = perbandingan sisi kotak gambar pada kartu, per kategori.

   KENAPA PER KATEGORI, BUKAN SATU UNTUK SEMUA. Ketiga jenis materi
   ini bentuknya memang berbeda: leaflet lipat tiga melebar, flyer
   media sosial tegak, poster lebih tegak lagi. Satu perbandingan
   untuk semuanya berarti dua dari tiga kategori pasti tampil salah -
   entah terpotong, entah tenggelam di tengah ruang kosong.

   Karena tab memisahkan ketiganya, kartu yang tampil bersamaan selalu
   satu kategori - jadi grid tetap rapi meski angkanya berbeda-beda.

   `lebarMin` = lebar terkecil sebuah kartu, dalam piksel. Dari angka
   ini grid menghitung sendiri berapa kolom yang muat - tidak ada
   jumlah kolom yang dipatok.

   KENAPA BEGITU. Kartu tegak dan kartu mendatar tidak boleh sama
   lebarnya. Pada lebar yang sama, kartu 9:16 jadi hampir dua kali
   lebih tinggi daripada kartu 3:2 - dan pada tiga kolom di layar
   lebar, tingginya melewati satu layar penuh untuk satu baris saja.
   Itu yang membuat tab Flyer terasa jauh lebih berat daripada tab
   Leaflet padahal isinya lebih sedikit.

   Flyer dipatok 250px supaya muat lebih banyak kolom dan tiap
   kartunya jadi lebih pendek; leaflet 340px karena ia melebar dan
   butuh lebar untuk terbaca.

   Kalau ada satu materi yang bentuknya menyimpang dari kategorinya,
   tambahkan `rasio: "1 / 1"` pada materi itu sendiri; nilai di materi
   menang atas nilai di kategori.
   ============================================================ */
export const KATEGORI = [
  { key: "leaflet", labelKey: "edukasi.leaflet", rasio: "3 / 2", lebarMin: 340 },
  { key: "flyer", labelKey: "edukasi.flyer", rasio: "9 / 16", lebarMin: 270 },
  { key: "poster", labelKey: "edukasi.poster", rasio: "5 / 7", lebarMin: 260 },
];

export const MATERI = [
  {
    id: "ket",
    kategori: "leaflet",
    judul: {
      id: "Kehamilan Ektopik Terganggu (KET)",
      en: "Ruptured Ectopic Pregnancy",
    },
    ringkas: {
      id: "Tanda awal, faktor risiko, dan kapan harus segera ke fasilitas kesehatan.",
      en: "Early signs, risk factors, and when to seek immediate care.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/1.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/2.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "mioma",
    kategori: "leaflet",
    judul: {
      id: "Mioma Uteri",
      en: "Uterine Fibroids",
    },
    ringkas: {
      id: "Jenis mioma berdasarkan letaknya, faktor risiko, dan kaitannya dengan kehamilan.",
      en: "Types of fibroids by location, risk factors, and how they relate to pregnancy.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/3.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/4.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "hemodialisa",
    kategori: "leaflet",
    judul: {
      id: "Hemodialisa (Cuci Darah)",
      en: "Haemodialysis",
    },
    ringkas: {
      id: "Proses hemodialisa, penyebab gagal ginjal, dan kapan seseorang perlu menjalaninya.",
      en: "How dialysis works, causes of kidney failure, and when it becomes necessary.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/5.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/6.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "kejang-demam",
    kategori: "leaflet",
    judul: {
      id: "Kejang Demam pada Anak",
      en: "Febrile Seizures in Children",
    },
    ringkas: {
      id: "Pertolongan pertama saat anak kejang, tanda bahaya, dan kapan harus dibawa ke IGD.",
      en: "First aid during a seizure, warning signs, and when to go to the emergency department.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/7.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/8.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "gerd",
    kategori: "leaflet",
    judul: {
      id: "GERD (Gastroesophageal Reflux Disease)",
      en: "GERD (Gastro-oesophageal Reflux Disease)",
    },
    ringkas: {
      id: "Gejala GERD, bedanya dengan maag biasa, dan tips mencegah kekambuhan.",
      en: "GERD symptoms, how it differs from ordinary indigestion, and how to prevent flare-ups.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/9.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/10.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "diare",
    kategori: "leaflet",
    judul: {
      id: "Diare",
      en: "Diarrhoea",
    },
    ringkas: {
      id: "Penyebab diare, tanda dehidrasi yang perlu diwaspadai, dan penanganannya.",
      en: "Causes of diarrhoea, signs of dehydration to watch for, and how to manage it.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/11.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/12.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "ketoasidosis",
    kategori: "leaflet",
    judul: {
      id: "Ketoasidosis Diabetikum",
      en: "Diabetic Ketoacidosis",
    },
    ringkas: {
      id: "Tanda bahaya pada pasien diabetes, penyebabnya, dan kapan harus segera ke IGD.",
      en: "Warning signs in people with diabetes, what causes them, and when to seek emergency care.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/13.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/14.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "stroke",
    kategori: "leaflet",
    judul: {
      id: "Stroke",
      en: "Stroke",
    },
    ringkas: {
      id: "Kenali gejala stroke sejak dini dan pentingnya penanganan dalam waktu kurang dari 2 jam.",
      en: "Recognising stroke symptoms early, and why treatment within two hours matters.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/15.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/16.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "capd",
    kategori: "leaflet",
    judul: {
      id: "CAPD (Continuous Ambulatory Peritoneal Dialysis)",
      en: "CAPD (Continuous Ambulatory Peritoneal Dialysis)",
    },
    ringkas: {
      id: "Cara kerja CAPD, siapa yang cocok menggunakannya, dan hal yang perlu diperhatikan.",
      en: "How CAPD works, who it suits, and what to watch out for.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan/17.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan/18.png", alt: { id: "", en: "" } },
    ],
  },

  /* ============================================================
     FLYER - dari public/images/edukasi-kesehatan-flyyer/

     TUJUH BERKAS, TIGA MATERI. Ketujuh berkas ini bukan tujuh flyer
     terpisah melainkan tiga rangkaian carousel Instagram - penamaan
     berkasmu sendiri yang menunjukkannya: "IGS IGD ATAU POLI 1" dan
     "2" satu rangkaian, "IGS GINJAL" 1-3 satu rangkaian, "IGS
     DIABETES" 1-2 satu rangkaian. Tulisan "Swipe untuk tahu lebih
     lanjut" di tiap gambar mengonfirmasinya.

     Digabung, pengunjung menekan satu kartu lalu berpindah antar
     lembar dengan panah - persis cara carousel-nya dibaca di
     Instagram. Penanda "3 halaman" di sudut kartu memberi tahu ada
     berapa lembar sebelum dibuka.

     JUDULNYA TIDAK MEMAKAI NAMA BERKASMU, dan ini sengaja. "IGS"
     singkatan Instagram Story, angka di belakangnya urutan unggahan -
     label kerja internal yang tidak berarti apa-apa bagi pasien.
     Karena satu kartu kini memuat beberapa lembar, angka urutan itu
     juga tidak lagi punya tempat: kartu "IGS Ginjal 1" yang isinya
     tiga lembar justru membingungkan.

     Kalau nanti berubah pikiran, memecahnya kembali jadi tujuh cukup
     memisahkan tiap isi `halaman` jadi entri sendiri.
     ============================================================ */
  {
    id: "flyer-igd-poli",
    kategori: "flyer",
    judul: {
      id: "IGD atau Poli?",
      en: "Emergency Department or Outpatient Clinic?",
    },
    ringkas: {
      id: "Kapan keluhan harus dibawa ke IGD, dan kapan cukup lewat poliklinik.",
      en: "When to go to the emergency department, and when the outpatient clinic is enough.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan-flyyer/1.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan-flyyer/2.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "flyer-ginjal",
    kategori: "flyer",
    judul: {
      id: "Obat Rutin dan Kesehatan Ginjal",
      en: "Long-term Medicines and Kidney Health",
    },
    ringkas: {
      id: "Mitos dan fakta seputar obat penyakit kronis, dan kenapa berhenti sendiri justru berisiko.",
      en: "Myths and facts about medicines for chronic illness, and why stopping on your own is risky.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan-flyyer/3.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan-flyyer/4.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan-flyyer/5.png", alt: { id: "", en: "" } },
    ],
  },
  {
    id: "flyer-diabetes",
    kategori: "flyer",
    judul: {
      id: "Mitos dan Fakta Diabetes",
      en: "Diabetes Myths and Facts",
    },
    ringkas: {
      id: "Benarkah tidak makan manis berarti aman dari diabetes, dan cara mencegahnya sejak dini.",
      en: "Does avoiding sweets really keep you safe from diabetes, and how to prevent it early.",
    },
    halaman: [
      { src: "/images/edukasi-kesehatan-flyyer/6.png", alt: { id: "", en: "" } },
      { src: "/images/edukasi-kesehatan-flyyer/7.png", alt: { id: "", en: "" } },
    ],
  },

  /* ============================================================
     POSTER - dari public/images/edukasi-kesehatan-poster/

     Judul dan ringkasan disusun dari isi posternya yang terbaca di
     tangkapan layar situs lama.

     ⚠️ URUTAN 1-7 mengikuti urutan tampil di situs itu - sama seperti
     leaflet, ini ASUMSI. Buka tab Poster sekali dan cocokkan judul
     tiap kartu dengan gambarnya.

     DUA LABEL YANG SAYA UBAH, dan alasannya:

     "Poster Hipertensi A3" -> "Poster Hipertensi". A3 adalah ukuran
     kertas cetaknya. Berguna bagi yang memesan cetakan, tidak berarti
     apa-apa bagi pasien yang membacanya di layar.

     "Poster Publik Menjaga Kesehatan Tubuh Ilustratif Ungu dan Putih"
     -> "Poster Maag (Dispepsia)". Nama panjang itu berasal dari nama
     TEMPLAT DESAIN, bukan judul materinya - ia menjelaskan warna dan
     gaya gambarnya, bukan isinya. Posternya sendiri berjudul "Yuk
     Kenalan dengan Maag (Dispepsia)".

     Kalau kamu ingin label aslinya yang tampil, ganti isi `judul`.
     ============================================================ */
  {
    id: "poster-bronkopneumonia",
    kategori: "poster",
    judul: {
      id: "Poster Bronkopneumonia pada Anak",
      en: "Bronchopneumonia in Children",
    },
    ringkas: {
      id: "Gejala, penanganan, dan kenapa menjauhkan anak dari asap rokok itu penting.",
      en: "Symptoms, treatment, and why keeping children away from cigarette smoke matters.",
    },
    halaman: [{ src: "/images/edukasi-kesehatan-poster/1.png", alt: { id: "", en: "" } }],
  },
  {
    id: "poster-hipertensi",
    kategori: "poster",
    judul: {
      id: "Poster Hipertensi",
      en: "Hypertension",
    },
    ringkas: {
      id: "Apa itu hipertensi, penyebabnya, gejalanya, dan akibat bila tidak terkontrol.",
      en: "What hypertension is, its causes and symptoms, and what happens if it goes uncontrolled.",
    },
    halaman: [{ src: "/images/edukasi-kesehatan-poster/2.png", alt: { id: "", en: "" } }],
  },
  {
    id: "poster-sesak-napas-anak",
    kategori: "poster",
    judul: {
      id: "Poster Sesak Napas pada Anak",
      en: "Shortness of Breath in Children",
    },
    ringkas: {
      id: "Penyebab sesak pada anak - asma, bronkopneumonia, infeksi saluran napas - dan penanganannya.",
      en: "Causes of breathing difficulty in children - asthma, bronchopneumonia, respiratory infection - and how they are treated.",
    },
    halaman: [{ src: "/images/edukasi-kesehatan-poster/3.png", alt: { id: "", en: "" } }],
  },
  {
    id: "poster-ppok-lansia",
    kategori: "poster",
    judul: {
      id: "Poster PPOK pada Lansia",
      en: "COPD in Older Adults",
    },
    ringkas: {
      id: "Gejala dan penyebab penyakit paru obstruktif kronik, serta apakah semua sesak pada lansia berarti PPOK.",
      en: "Symptoms and causes of chronic obstructive pulmonary disease, and whether every breathing problem in older adults is COPD.",
    },
    halaman: [{ src: "/images/edukasi-kesehatan-poster/4.png", alt: { id: "", en: "" } }],
  },
  {
    id: "poster-krisis-hipertensi",
    kategori: "poster",
    judul: {
      id: "Poster Krisis Hipertensi",
      en: "Hypertensive Crisis",
    },
    ringkas: {
      id: "Kapan tekanan darah tinggi disebut krisis, tanda yang menyertainya, dan pencegahan jangka panjang.",
      en: "When high blood pressure becomes a crisis, the warning signs, and long-term prevention.",
    },
    halaman: [{ src: "/images/edukasi-kesehatan-poster/5.png", alt: { id: "", en: "" } }],
  },
  {
    id: "poster-maag",
    kategori: "poster",
    judul: {
      id: "Poster Maag (Dispepsia)",
      en: "Indigestion (Dyspepsia)",
    },
    ringkas: {
      id: "Penyebab dan gejala dispepsia, serta penanganannya lewat pola makan, gaya hidup, dan obat.",
      en: "Causes and symptoms of dyspepsia, and how it is managed through diet, lifestyle, and medication.",
    },
    halaman: [{ src: "/images/edukasi-kesehatan-poster/6.png", alt: { id: "", en: "" } }],
  },
  {
    id: "poster-batu-empedu",
    kategori: "poster",
    judul: {
      id: "Poster Batu Empedu",
      en: "Gallstones",
    },
    ringkas: {
      id: "Bagaimana batu empedu terbentuk, gejalanya, dan apakah selalu harus dioperasi.",
      en: "How gallstones form, their symptoms, and whether surgery is always required.",
    },
    halaman: [{ src: "/images/edukasi-kesehatan-poster/7.png", alt: { id: "", en: "" } }],
  },

];
